import { useState, useCallback } from 'react';
import { obtenerMatchMode } from '../utils/TableTemplates';

/**
 * Función auxiliar para generar la estructura base del filtro
 */
const generarFiltrosDesdeModelo = (model) => {
    if (!model) return {};
    const filters = {};
    Object.keys(model).forEach((key) => {
        const config = model[key];
        // Solo procesamos si tiene la propiedad filter: true
        if (config.filter) {
            const fieldName = config.filterField || key; // Prioridad a filterField si existe
            // Determinamos el MatchMode por defecto según el tipo de dato
            const matchMode = obtenerMatchMode(config);
            filters[fieldName] = {
                value: null,
                matchMode: matchMode
            };
        }
    });
    return filters;
};

/**
 * Hook personalizado para manejar el estado de DataTables de PrimeReact con Lazy Loading.
 * Maneja automáticamente: Paginación, Ordenamiento, Filtros y Reset de página al filtrar.
 * 
 * @param {Object} initialConfig Configuración inicial (opcional)
 * @param {string} initialConfig.initialSortField Campo por defecto para ordenar
 * @param {number} initialConfig.initialSortOrder Orden por defecto (1: asc, -1: desc)
 * @param {number} initialConfig.initialRows Filas por página (default: 10)
 */
export const useLazyParams = (initialConfig = {}) => {

    // POR DEFECTO
    const {
        initialSortField = null,
        initialSortOrder = null,
        initialRows = parseInt(process.env.REACT_APP_ROWS_NUM || 10),
        modelo = null // Necesario para inicializar filtros
    } = initialConfig;

    const filtrosIniciales = generarFiltrosDesdeModelo(modelo);

    // ESTADOS
    const [loading, setLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [data, setData] = useState([]);

    // Estado unificado compatible con PrimeReact LazyLoad
    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: initialRows,
        page: 0,
        sortField: initialSortField,
        sortOrder: initialSortOrder,
        filters: filtrosIniciales
    });

    /**
     * Función unificada que recibe los eventos onPage, onSort, onFilter de PrimeReact.
     * Detecta si cambiaron los filtros para resetear la paginación a la página 1.
     */
    const onLazyChange = useCallback((event) => {
        setLazyParams((prev) => {
            // Detectamos si el evento es un cambio de filtros
            // (PrimeReact envía 'filters' en el evento cuando se filtra)
            const isFilterChange = event.filters && JSON.stringify(event.filters) !== JSON.stringify(prev.filters);

            return {
                ...prev, // Mantenemos el estado anterior
                ...event, // Sobrescribimos con los nuevos datos del evento
                // Si cambiaron los filtros, forzamos volver a la página 0 (first: 0)
                first: isFilterChange ? 0 : event.first,
                page: isFilterChange ? 0 : event.page
            };
        });
    }, []);

    // Helper para refrescar manualmente la tabla (ej: después de guardar/editar y mantener filtros)
    // "toca" el estado para disparar el useEffect en el componente padre
    const refreshData = () => {
        setLazyParams(prev => ({ ...prev }));
    };

    // Función que permite refrescar las filas de primereact dado que cambia la información de la fila forzando un re render
    const refreshRows = useCallback(() => {
        const now = Date.now();
        setData(prev => prev.map(row => ({ ...row, __uiRefresh: now }))
        );
    }, []);

    const resetLazyParams = useCallback(() => {
        setLazyParams({
            first: 0,
            rows: initialRows,
            page: 0,
            sortField: initialSortField,
            sortOrder: initialSortOrder,
            filters: generarFiltrosDesdeModelo(modelo)
        });
    }, [initialRows, initialSortField, initialSortOrder, modelo]);

    return {
        // Estados de Datos
        loading,
        setLoading,
        data,
        setData,
        totalRecords,
        setTotalRecords,

        // Estado de la Tabla
        lazyParams,
        setLazyParams,

        // Funciones
        onLazyChange, // Conecta onPage, onSort, onFilter
        refreshData,
        refreshRows,
        resetLazyParams,
    };
};